import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Store, Copy, Check, ExternalLink, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const VENUE_TYPES = [
  { value: 'bar', label: 'Bar' },
  { value: 'club', label: 'Club' },
  { value: 'restaurant', label: 'Restaurant' },
  { value: 'event', label: 'Event / Party' },
  { value: 'festival', label: 'Festival' },
  { value: 'hotel', label: 'Hotel' },
  { value: 'lounge', label: 'Lounge' },
  { value: 'other', label: 'Other' },
];

export default function VenuePartnerSignupPage() {
  const [form, setForm] = useState({ name: '', type: 'bar', city: '', country: '', contact_email: '' });
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/venue-partners/register`, form);
      setResult(r.data);
      // Persist for this device so next /verify-badge visit auto-attributes
      localStorage.setItem('bhermes_venue_code', r.data.partner_code);
      localStorage.setItem('bhermes_venue_name', r.data.name);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Registration failed');
    } finally {
      setSubmitting(false);
    }
  };

  const scannerUrl = result ? `${window.location.origin}/verify-badge?venue=${result.partner_code}` : '';

  const copyUrl = () => {
    navigator.clipboard.writeText(scannerUrl);
    setCopied(true);
    toast.success('Scanner link copied');
    setTimeout(() => setCopied(false), 2000);
  };

  if (result) {
    return (
      <div className="min-h-screen bg-[#0a0e1a] text-zinc-100 flex flex-col" data-testid="venue-signup-success">
        <header className="border-b border-zinc-800/60 px-5 py-4">
          <Link to="/" className="text-xs tracking-widest text-zinc-400 hover:text-zinc-200">BHERMES · Venue Partner</Link>
        </header>
        <main className="flex-1 px-5 py-10 max-w-md w-full mx-auto">
          <Card className="bg-gradient-to-br from-emerald-600 to-emerald-700 border-0">
            <CardContent className="p-8 space-y-5 text-white">
              <div className="text-center">
                <Check className="w-16 h-16 mx-auto" />
                <h1 className="text-2xl font-bold mt-2">{result.name} is live</h1>
                <p className="text-emerald-50 text-sm mt-1">You're now a BHERMES verification partner.</p>
              </div>
              <div className="bg-black/25 rounded-lg p-4">
                <p className="text-[10px] tracking-widest opacity-70">YOUR PARTNER CODE</p>
                <p className="text-3xl font-mono font-bold" data-testid="partner-code">{result.partner_code}</p>
              </div>
              <div className="bg-black/25 rounded-lg p-4 space-y-2">
                <p className="text-[10px] tracking-widest opacity-70">YOUR SCANNER LINK</p>
                <code className="text-xs break-all block" data-testid="scanner-url">{scannerUrl}</code>
                <div className="flex gap-2 pt-2">
                  <Button size="sm" variant="secondary" className="flex-1 bg-white text-emerald-700" onClick={copyUrl} data-testid="copy-scanner-url">
                    <Copy className="w-3.5 h-3.5 mr-1.5" /> {copied ? 'Copied' : 'Copy'}
                  </Button>
                  <a href={scannerUrl} target="_blank" rel="noreferrer" className="flex-1">
                    <Button size="sm" variant="secondary" className="w-full bg-white text-emerald-700" data-testid="open-scanner">
                      <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Open
                    </Button>
                  </a>
                </div>
              </div>
              <p className="text-xs text-emerald-100/90 leading-relaxed">
                Bookmark the scanner link on your phone's home screen. Every scan you make is counted
                toward guests' "Trusted by N venues" score — the more active you are, the more guests
                trust your door.
              </p>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0e1a] text-zinc-100 flex flex-col" data-testid="venue-signup-page">
      <header className="border-b border-zinc-800/60 px-5 py-4 flex items-center justify-between">
        <Link to="/verify-badge" className="text-xs tracking-widest text-zinc-400 hover:text-zinc-200 flex items-center gap-1">
          <ArrowLeft className="w-3.5 h-3.5" /> BHERMES
        </Link>
        <span className="text-[10px] tracking-widest text-zinc-500">PARTNER PROGRAM</span>
      </header>

      <main className="flex-1 px-5 py-10 max-w-md w-full mx-auto">
        <div className="text-center mb-6 space-y-2">
          <div className="w-14 h-14 rounded-xl bg-amber-500/15 flex items-center justify-center mx-auto">
            <Store className="w-7 h-7 text-amber-400" />
          </div>
          <h1 className="text-3xl font-semibold">Become a Verification Partner</h1>
          <p className="text-sm text-zinc-400">
            Free forever. Every badge you scan counts toward a guest's on-chain trust score.
          </p>
        </div>

        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-6">
            <form onSubmit={submit} className="space-y-4" data-testid="venue-signup-form">
              <Field label="Venue name">
                <Input value={form.name} onChange={(e) => update('name', e.target.value)}
                  placeholder="The Velvet Room" required data-testid="venue-name-input"
                  className="bg-zinc-800 border-zinc-700" />
              </Field>

              <Field label="Venue type">
                <Select value={form.type} onValueChange={(v) => update('type', v)}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700" data-testid="venue-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                    {VENUE_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>

              <div className="grid grid-cols-2 gap-3">
                <Field label="City">
                  <Input value={form.city} onChange={(e) => update('city', e.target.value)}
                    placeholder="Lisbon" required data-testid="venue-city-input"
                    className="bg-zinc-800 border-zinc-700" />
                </Field>
                <Field label="Country">
                  <Input value={form.country} onChange={(e) => update('country', e.target.value)}
                    placeholder="Portugal" required data-testid="venue-country-input"
                    className="bg-zinc-800 border-zinc-700" />
                </Field>
              </div>

              <Field label="Contact email">
                <Input type="email" value={form.contact_email}
                  onChange={(e) => update('contact_email', e.target.value)}
                  placeholder="door@velvetroom.com" required
                  data-testid="venue-email-input"
                  className="bg-zinc-800 border-zinc-700" />
              </Field>

              <Button type="submit" disabled={submitting} size="lg"
                className="w-full bg-amber-500 hover:bg-amber-600 text-black" data-testid="venue-submit-btn">
                {submitting ? 'Registering…' : 'Get my partner code'}
              </Button>
            </form>

            <p className="text-[11px] text-zinc-500 mt-4 leading-relaxed">
              We store only what you enter above. No scan-by-scan guest identities are ever shared with
              other venues.
            </p>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs tracking-widest text-zinc-400">{label.toUpperCase()}</Label>
      {children}
    </div>
  );
}
